/* eslint-disable no-nested-ternary */
import { ReceiptLong } from '@mui/icons-material';
import { Avatar, Box, IconButton, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { Order } from '../../apiTypes';
import DataTable, { Column } from '../../components/DataTable';
import OrderHistoryButton from '../../components/History/OrderHistoryButton';
import { t } from '../../constants/translations';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { dateLocaleMap } from '../../utils/dates';

import BillPrintModal from '../POS/BillPrintModal';

type Row = DbDocument<Order>;

const ActiveOrders = () => {
  const { lang } = useLocale();
  const { workersMap, worker, orders: data } = useCafe();

  const [bill, setBill] = useState<DbDocument<Order> | null>(null);

  const orders = useMemo(
    () => (worker?.credentials.view_archive_of_other_employees ? data : data?.filter((o) => o.workerId === worker?.id)),
    [data, worker?.credentials.view_archive_of_other_employees, worker?.id]
  );

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'number',
        title: t.number[lang],
        align: 'left',
        render: (r: Row) => <Typography>{r.number}</Typography>,
      },
      {
        name: 'time',
        title: t.creation_time[lang],
        align: 'center',
        render: (r: Row) => (
          <Typography>{format(new Date(r.created), 'dd MMM HH:mm', { locale: dateLocaleMap[lang] })}</Typography>
        ),
      },
      {
        name: 'tableName',
        title: t.table[lang],
        align: 'center',
        render: (r: Row) => <Typography>{r.tableName}</Typography>,
        filterable: true,
      },
      {
        name: 'workerName',
        title: t.author[lang],
        align: 'center',
        render: (r: Row) => (
          <Stack direction="row" alignItems="center" spacing={{ xs: 1 }}>
            <Avatar src={workersMap[r.workerId]?.photoUrl} />
            <span>{workersMap[r.workerId]?.name || r.workerName || `${r.clientName} (${t.client[lang]})`}</span>
          </Stack>
        ),
        filterable: true,
      },
      {
        name: 'bill',
        title: '',
        align: 'right',
        render: (r: Row) => (
          <IconButton onClick={() => setBill(r)} disabled={!worker?.credentials.print_bill}>
            <ReceiptLong />
          </IconButton>
        ),
      },
      {
        name: 'history',
        title: '',
        align: 'right',
        render: (r: Row) => <OrderHistoryButton order={r} />,
      },
    ],
    [lang, worker?.credentials.print_bill, workersMap]
  );

  return (
    <Box sx={{ maxWidth: 800, m: '0 auto', pb: 2 }}>
      <DataTable columns={columns} rows={orders || []} perPage={10} dataTestIdPrefix="order" />

      {bill && (
        <BillPrintModal
          service={bill.paid?.service || 0}
          discount={bill.paid?.discount || 0}
          onClose={() => setBill(null)}
          order={bill}
          promotionsApplied={bill.paid?.promotionsApplied || []}
        />
      )}
    </Box>
  );
};

export default ActiveOrders;
